import {
  faBars,
  faCog,
  faCompressAlt,
  faExpandAlt,
  faSignInAlt,
  faUser,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FC } from 'react';
import { lazy, Suspense } from 'react';
import { Link as RouterLink, useLocation, useMatch } from 'react-router-dom';
import type { OptionalAssignmentServiceRequest } from 'src/apis/assignment-service/use-optional-assignment-service';
import type { OptionalAuthorizationServiceRequest } from 'src/apis/authorization-service/use-authorization-service';
import type { CapacityManagementRootResponse } from 'src/apis/capacity-management/types';
import type { DirectFcfsEntrypoint } from 'src/apis/direct-fcfs/types';
import type { HydrogenFcfsRoot } from 'src/apis/hydrogen-fcfs/types';
import type { OrganisationServiceRequest } from 'src/apis/organisation-service/use-organisation-service';
import type { MapPrototypeRequest } from 'src/apis/prototype-map/use-map-prototype';
import type { RegularFcfsRoot } from 'src/apis/regular-fcfs/types';
import type { RemitReportingEntrypoint } from 'src/apis/remit-reporting/types';
import type { OptionalStorageServiceRootRequest } from 'src/apis/storage/use-storage-service-root';
import { ActionModeProvider } from 'src/components/action-mode';
import { Button } from 'src/components/buttons-and-actions/button';
import { DropdownTrigger } from 'src/components/buttons-and-actions/dropdown-trigger';
import { Container } from 'src/components/layout/container';
import {
  HeaderPrimaryBackground,
  HeaderPrimaryCenter,
} from 'src/components/layout/header/primary-nav';
import type { ActiveSection } from 'src/components/layout/header/types';
import type { MobileNavState } from 'src/components/layout/header/use-mobile-nav';
import type { UserMenuProps } from 'src/components/layout/header/user-menu';
import { UserMenu } from 'src/components/layout/header/user-menu';
import { Logo } from 'src/components/media-and-icons/logo';
import { Link } from 'src/components/navigation/link';
import { useAccounts } from 'src/hooks/use-accounts';
import type { Successful } from 'src/hooks/use-axios';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import { useCognitoUser } from 'src/hooks/use-cognito-user';
import { useFullwidth } from 'src/hooks/use-fullwidth';
import { useGmdFlags } from 'src/hooks/use-gdm-flags';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { useReauthenticate } from 'src/hooks/use-reauthenticate';
import { useDeveloperTools } from 'src/pages/developer-tools/use-developer-tools';
import type { StrictOmit } from 'src/utils/utility-types';

const DeveloperToolsIcon = lazy(() => import('src/pages/developer-tools/icon'));

type Props = {
  activeSection: ActiveSection;
  authorizationService?: Successful<OptionalAuthorizationServiceRequest>;
  organisationService?: Successful<OrganisationServiceRequest>;
  capacityManagementService?: CapacityManagementRootResponse;
  regularFcfsService?: RegularFcfsRoot;
  directFcfsService?: DirectFcfsEntrypoint;
  hydrogenFcfsRoot?: HydrogenFcfsRoot;
  mobileNav: MobileNavState;
  remitReportingService?: RemitReportingEntrypoint;
  assignmentService?: Successful<OptionalAssignmentServiceRequest>;
  mapPrototype?: Successful<MapPrototypeRequest>;
  storageServiceRoot?: Successful<OptionalStorageServiceRootRequest>;
};

export const HeaderPrimary: FC<Props> = ({
  activeSection,
  authorizationService,
  organisationService,
  capacityManagementService,
  regularFcfsService,
  directFcfsService,
  hydrogenFcfsRoot,
  mobileNav,
  assignmentService,
  remitReportingService,
  mapPrototype,
  storageServiceRoot,
}) => {
  const [fullwidth, setFullwidth] = useFullwidth();
  const { minTablet, minDesktop, minWideDesktop } = useBreakpoints();
  const monolithUser = useOptionalAuthenticatedMonolithUser();
  const cognitoUser = useCognitoUser();
  const isAuthenticated = Boolean(cognitoUser);
  const gdmFlags = useGmdFlags();
  const isGdmSection = useMatch('/gdm/*');
  const { pathname, search } = useLocation();
  const [reauthenticate] = useReauthenticate();
  const accounts = useAccounts();

  const userMenuData: StrictOmit<UserMenuProps, 'show'> = {
    organisationService: organisationService?.response.data ?? null,
    authorizationService: authorizationService?.response.data ?? null,
    capacityManagementService: capacityManagementService ?? null,
    regularFcfsService,
    directFcfsService,
    hydrogenFcfsRoot,
    assignmentService: assignmentService?.response.data ?? null,
    remitReportingService,
  };

  const sectionLinks = (
    <>
      {(!monolithUser || monolithUser.role !== 'SSO_ADMIN') && (
        <Link
          mode="primary"
          to="/transport/auctions"
          isActive={activeSection.transport}
          data-help-annotation="transport:1"
        >
          Transport
        </Link>
      )}

      <Link
        mode="primary"
        to={
          storageServiceRoot?.response.data._links?.getSsoOfferCards
            ? '/storage/offer-cards'
            : '/storage/offers'
        }
        isActive={activeSection.storage}
      >
        Storage
      </Link>

      <Link mode="primary" to="/lng" isActive={activeSection.lng}>
        LNG
      </Link>

      {hydrogenFcfsRoot && (
        <Link mode="primary" to="/h2" isActive={activeSection.h2}>
          Hydrogen
        </Link>
      )}

      <Link mode="primary" to="/reporting" isActive={activeSection.reporting}>
        Reporting
      </Link>

      <Link
        mode="primary"
        to="/market-messages"
        isActive={activeSection.marketMessages}
        data-help-annotation="umm-overview:1 umm-rss:1"
      >
        Market Messages
      </Link>

      {(gdmFlags.userCanAcces || isGdmSection) && (
        <Link mode="primary" to="/gdm" isActive={activeSection.gdm}>
          GDM
        </Link>
      )}

      {/* ! Implementation for the Map Prototype */}
      {mapPrototype?.response && (
        <Link mode="primary" to="/prototype-map">
          [Prototype] Map
        </Link>
      )}
    </>
  );

  const login = (
    <>
      {!reauthenticate && (
        <Link
          mode="primary"
          to="/registration/wizard/start"
          data-help-annotation="register-start:1"
        >
          Register
        </Link>
      )}

      <Link
        mode={minTablet ? 'button-inverse' : 'primary'}
        to={
          // if someone presses on the login button from _within_ the login
          // section, we just want to reuse the search params
          pathname.startsWith('/login')
            ? { pathname: '/login', search }
            : {
                pathname: '/login',
                search: `redirectUrl=${encodeURIComponent(
                  location.href.replace('/angular', '/platform')
                )}`,
              }
        }
        data-help-annotation="unauthenticated:1"
      >
        {minTablet && !minDesktop ? (
          <FontAwesomeIcon icon={faSignInAlt} aria-label="Log in" />
        ) : (
          <>Log in</>
        )}
      </Link>
    </>
  );

  const developerTools = useDeveloperTools();

  return (
    <HeaderPrimaryBackground>
      <Container>
        <HeaderPrimaryCenter>
          <div
            className={`
              grid grid-cols-[auto_1fr] items-center
              legacy:gap-[2rem]
              legacy:lg:gap-[3.4rem]
            `}
          >
            <RouterLink
              to={
                monolithUser?.isTso
                  ? '/platform/#/start'
                  : cognitoUser
                    ? '/dashboard'
                    : '/'
              }
            >
              <Logo />
            </RouterLink>

            <div className="animate-fade-in">
              {minTablet && (
                <div
                  className={`
                    flex flex-wrap content-normal items-center
                    legacy:gap-x-[2rem] legacy:gap-y-[0.5rem]
                    legacy:lg:gap-x-[3.4rem] legacy:lg:gap-y-[0.5rem]
                  `}
                >
                  {sectionLinks}
                </div>
              )}
            </div>
          </div>

          <div className="animate-fade-in">
            <div
              className={`
                grid grid-flow-col items-center gap-xl
                legacy:gap-[2rem]
              `}
            >
              {!minTablet && (
                <Button
                  onClick={() =>
                    mobileNav.setValue(
                      mobileNav.value === 'primary' ? null : 'primary'
                    )
                  }
                >
                  <FontAwesomeIcon
                    icon={faBars}
                    aria-label="Toggle Main Navigation"
                  />
                </Button>
              )}

              {minTablet &&
                (isAuthenticated && !reauthenticate ? (
                  <>
                    <DropdownTrigger
                      data-testid="toggle-settings-menu"
                      data-help-annotation="settings-menu:1"
                      label={
                        <FontAwesomeIcon
                          icon={faCog}
                          aria-label="Toggle Settings Menu"
                        />
                      }
                    >
                      <UserMenu
                        {...userMenuData}
                        show={{
                          personalSettings: false,
                          companySettings: true,
                          transportSettings: true,
                          storageSettings: true,
                          shipperSettings: true,
                          otherSettings: false,
                          reportingSettings: true,
                        }}
                      />
                    </DropdownTrigger>

                    <DropdownTrigger
                      label={
                        <div
                          className={`
                            grid max-w-[16rem] grid-flow-col items-center
                            text-left
                            legacy:gap-[0.5rem]
                          `}
                        >
                          <FontAwesomeIcon
                            icon={faUser}
                            aria-label="Toggle User Menu"
                          />
                          {accounts.current && minDesktop && (
                            <div
                              className={`
                                flex flex-col overflow-hidden text-legacy-xs
                              `}
                            >
                              {accounts.current.organisationName && (
                                <div className="grid items-center">
                                  <strong className="truncate">
                                    {accounts.current.organisationName}
                                  </strong>
                                </div>
                              )}
                              <span
                                className={`
                                  truncate text-legacy-brand-light-2
                                  transition-colors duration-350
                                  in-[button:hover]:text-legacy-brand-secondary
                                  in-[button[aria-expanded=true]]:text-legacy-brand-secondary
                                `}
                              >
                                {accounts.current.email}
                              </span>
                            </div>
                          )}
                        </div>
                      }
                      data-testid="toggle-user-menu"
                      data-help-annotation="authenticated:1"
                    >
                      <UserMenu
                        {...userMenuData}
                        show={{
                          personalSettings: true,
                          companySettings: false,
                          transportSettings: false,
                          storageSettings: false,
                          shipperSettings: false,
                          otherSettings: true,
                          reportingSettings: false,
                        }}
                      />
                    </DropdownTrigger>
                  </>
                ) : (
                  login
                ))}

              {minWideDesktop && (
                <Button
                  mode="icon-inverse"
                  onClick={() => setFullwidth(!fullwidth)}
                >
                  {fullwidth ? (
                    <FontAwesomeIcon
                      icon={faCompressAlt}
                      aria-label="Decrease width of website"
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faExpandAlt}
                      aria-label="Increase width of website"
                    />
                  )}
                </Button>
              )}
              {minWideDesktop && developerTools && (
                <Suspense fallback={<div className="w-[18px]" />}>
                  <DeveloperToolsIcon />
                </Suspense>
              )}
            </div>
          </div>
        </HeaderPrimaryCenter>
      </Container>

      {!minTablet && mobileNav.value === 'primary' && (
        <div
          className={`
            grid border-t border-legacy-brand
            legacy:gap-[1rem] legacy:px-[1.5rem] legacy:py-[1rem]
          `}
          onClick={() => mobileNav.setValue(null)}
        >
          <ActionModeProvider value={{ mode: 'link-primary' }}>
            {sectionLinks}
            {isAuthenticated && !reauthenticate ? (
              <UserMenu
                {...userMenuData}
                show={{
                  personalSettings: true,
                  companySettings: true,
                  transportSettings: true,
                  storageSettings: true,
                  shipperSettings: true,
                  otherSettings: true,
                  reportingSettings: true,
                }}
              />
            ) : (
              <>
                {!reauthenticate && (
                  <Link to="/registration/wizard/start">Register</Link>
                )}
                <Link
                  to={
                    // if someone presses on the login button from _within_ the login
                    // section, we just want to reuse the search params
                    pathname.startsWith('/login')
                      ? { pathname: '/login', search }
                      : {
                          pathname: '/login',
                          search: `redirectUrl=${encodeURIComponent(
                            location.href
                          )}`,
                        }
                  }
                >
                  Log in
                </Link>
              </>
            )}
          </ActionModeProvider>
        </div>
      )}
    </HeaderPrimaryBackground>
  );
};
